/**
 * Sales order contracts (S2.1 rewrite) — Board D #2.
 *
 * Fixed decisions encoded here (holistic plan §S2.1):
 * - Status chain: draft → confirmed → converted_to_po → shipped → invoiced
 *   → paid (草稿/已確認/已轉PO/已出貨/已開發票/已收款).
 * - Header: soNumber auto `SO-YYYYMM-seq`; `externalOrderNumber` is the
 *   searchable IMPORT DEDUPE KEY (re-import same number = UPDATE, never a
 *   duplicate); 預計出貨日 expectedShipDate + 交貨日 deliveryDate are required
 *   AT CONFIRM (draft entry stays permissive so intake is never blocked);
 *   `priceStatus` price-deviation state.
 * - Lines: productId is Ready-gated (only 產品就緒 products enter SO);
 *   `priceTrace` is a PERSISTED IMMUTABLE snapshot of the full resolver
 *   result at pricing time (the anti-175% artifact — never silently
 *   repriced); `estMargin = unitPrice − landedCost` (Board D profit).
 * - Price deviation beyond threshold → `pending_approval`, which blocks
 *   fulfillment/invoicing NOT entry (create/confirm still allowed).
 * - Low Margin Hold (2026-09-15, SO guide §3 / PRD §engines): a line whose
 *   margin on price is STRICTLY below the company minimum margin
 *   (`PricingDataset.minimumMarginPct`) raises the SAME `pending_approval`
 *   hold with reason `low_margin`; `priceHoldReasons` names every reason so
 *   the banner never says "deviates" for a margin hold. The floor tested is
 *   snapshotted on the header (`marginFloorPct`) — a later change to the
 *   company floor never silently re-judges a saved order.
 * - Import is ALL-OR-NOTHING per SO: any UNMATCHED_PART_NO fails the whole
 *   SO into the 訂單異常 exception queue — never silent product creation.
 * - Confirm checklist explicitly does NOT include customer-item
 *   authorization (dormant columns, NOT-in-build).
 */

import type { PriceResolution } from './pricing';

// ---------------------------------------------------------------------------
// Status
// ---------------------------------------------------------------------------

/** 草稿 / 已確認 / 已轉PO / 已出貨 / 已開發票 / 已收款 */
export type SalesOrderStatus =
  | 'draft'
  | 'confirmed'
  | 'converted_to_po'
  | 'shipped'
  | 'invoiced'
  | 'paid';

export const SALES_ORDER_STATUS_ORDER: readonly SalesOrderStatus[] = [
  'draft',
  'confirmed',
  'converted_to_po',
  'shipped',
  'invoiced',
  'paid',
] as const;

/**
 * Price-deviation state. `pending_approval` blocks fulfillment/invoicing,
 * NOT entry — the SO can still be created, edited and confirmed.
 */
export type SalesOrderPriceStatus = 'ok' | 'pending_approval';

/**
 * Why an order sits (or sat) in price review. `deviation` = a transacted
 * price beyond `SO_PRICE_DEVIATION_THRESHOLD_PCT` of the resolved price;
 * `low_margin` = a line's margin on price strictly below the company
 * minimum margin at pricing time. Both can fire on one order.
 */
export type SalesOrderPriceHoldReason = 'deviation' | 'low_margin';

export const SALES_ORDER_PRICE_HOLD_REASONS: readonly SalesOrderPriceHoldReason[] = [
  'deviation',
  'low_margin',
] as const;

export type SalesOrderSource = 'manual' | 'netsuite' | 'excel';

/**
 * Deviation threshold (percent of the resolved price) beyond which a manual
 * or imported unit price flips the SO to `pending_approval`. Config-stub
 * until a real pricing-settings row lands (W9 定價設定).
 */
export const SO_PRICE_DEVIATION_THRESHOLD_PCT = 5;

// ---------------------------------------------------------------------------
// Lines
// ---------------------------------------------------------------------------

export interface SalesOrderLine {
  id: string;
  /** Ready-gated: only 產品就緒 products may enter an SO line. */
  productId: string;
  /** Display mirror of the product SKU at snapshot time. */
  sku: string;
  /** Display mirror of the product display name at snapshot time. */
  productName: string;
  /** Customer PN cross-ref the line was entered/imported under, if any. */
  customerPartNumber?: string | null;
  qty: number;
  /** The transacted unit price (resolved, or manually overridden w/ reason). */
  unitPrice: number;
  currency: string;
  /**
   * System-resolved unit price at snapshot time — kept alongside the
   * transacted price so deviation stays auditable. `null` = unresolvable
   * (no cost record / no rule) at pricing time.
   */
  resolvedUnitPrice: number | null;
  /**
   * PERSISTED IMMUTABLE snapshot of the full resolver result at pricing
   * time (winner, trace[], waterfall[], marginVsLanded). Never mutated,
   * never silently repriced — a re-price is a NEW snapshot on an explicit
   * line update. `null` only for legacy/manual lines priced before the
   * resolver existed.
   */
  priceTrace: PriceResolution | null;
  /** Mandatory business reason when unitPrice ≠ resolvedUnitPrice (manual entry). */
  overrideReason?: string | null;
  /** Optional post-price discount percent (5 = −5%). */
  discountPct?: number;
  /** qty × unitPrice × (1 − discountPct/100), ROUND_HALF_UP 2dp. */
  lineTotal: number;
  /** Landed-cost snapshot from the pricing waterfall; null when no cost record. */
  landedCost: number | null;
  /** unitPrice − landedCost (Board D profit), 2dp; null when landedCost unknown. */
  estMargin: number | null;
  /** estMargin / unitPrice × 100, 2dp; null when estMargin is null. */
  estMarginPct: number | null;
}

// ---------------------------------------------------------------------------
// Header
// ---------------------------------------------------------------------------

export interface SalesOrderPriceApproval {
  approvedBy: string;
  reason: string;
  approvedAt: string;
}

/**
 * A price-review release that a later Modify voided (2026-09-16). Modify
 * re-prices the lines and voids the live release (`priceApproval: null`);
 * without this record the release — who, why, against which reasons and
 * which floor — vanished from the audit the moment anyone edited the order.
 * Append-only.
 */
export interface SalesOrderPriceApprovalRecord extends SalesOrderPriceApproval {
  /** The reasons the released hold carried. */
  holdReasons: SalesOrderPriceHoldReason[];
  /** The floor that hold was tested against (the header snapshot). */
  marginFloorPct: number | null;
  /** When the Modify that voided this release landed. */
  voidedAt: string;
}

export interface SalesOrder {
  id: string;
  /** Auto-assigned `SO-YYYYMM-seq` (per-month sequence), never enterable. */
  soNumber: string;
  customerId: number;
  /** Display mirror of the customer name at creation time. */
  customerName: string;
  status: SalesOrderStatus;
  source: SalesOrderSource;
  /**
   * Customer/NetSuite order number — the searchable IMPORT DEDUPE KEY:
   * re-importing the same externalOrderNumber UPDATES the existing SO.
   */
  externalOrderNumber: string | null;
  orderDate: string;
  /** 預計出貨日 — required at confirm (checklist `required_fields`). */
  expectedShipDate: string | null;
  /** 交貨日 — required at confirm (checklist `required_fields`). */
  deliveryDate: string | null;
  currency: string;
  /** Derived from line deviations + the margin floor; released via price approval (role-gated). */
  priceStatus: SalesOrderPriceStatus;
  /**
   * Every reason the hold was raised for (empty when nothing fired). Kept
   * after release so the audit trail still names what was released; a line
   * update re-derives it (and voids the release).
   */
  priceHoldReasons: SalesOrderPriceHoldReason[];
  /**
   * The company minimum margin (percent of sell price) in force when the
   * lines were priced — the number the `low_margin` reason was tested
   * against. `null` = no floor was configured at pricing time (legacy
   * orders), so no line can be shown "below floor" retroactively.
   */
  marginFloorPct: number | null;
  /** Set when a pending_approval hold was released. */
  priceApproval: SalesOrderPriceApproval | null;
  /** Releases voided by a later Modify — append-only; absent ⇒ none. */
  priceApprovalHistory?: SalesOrderPriceApprovalRecord[];
  lines: SalesOrderLine[];
  /** Σ lineTotal, 2dp. */
  totalAmount: number;
  /** Σ estMargin × qty over lines with known landed cost; null when none known. */
  totalEstMargin: number | null;
  linkedPoIds?: string[];
  createdAt: string;
  updatedAt: string;
}

// ---------------------------------------------------------------------------
// Confirm checklist (S2.1 handler rule — surfaced by the S2.3 checklist card)
// ---------------------------------------------------------------------------

/**
 * The confirm gate — explicitly NO customer-item authorization item.
 * `credit` is a stub (always passes) until a credit module lands.
 */
export type ConfirmChecklistKey =
  | 'customer_active'
  | 'products_ready'
  | 'price_resolvable'
  | 'credit'
  | 'required_fields';

export const CONFIRM_CHECKLIST_KEYS: readonly ConfirmChecklistKey[] = [
  'customer_active',
  'products_ready',
  'price_resolvable',
  'credit',
  'required_fields',
] as const;

export interface ConfirmChecklistItem {
  key: ConfirmChecklistKey;
  ok: boolean;
  /**
   * Machine-readable blocker refs (product ids, field names …) for the UI
   * to render localized detail rows. Empty when ok.
   */
  blockers: string[];
}

export interface ConfirmChecklistResult {
  ok: boolean;
  items: ConfirmChecklistItem[];
}

// ---------------------------------------------------------------------------
// Requests / responses
// ---------------------------------------------------------------------------

export interface SalesOrderListParams {
  page?: number;
  pageSize?: number;
  /** Matches soNumber, externalOrderNumber (dedupe key) and customerName. */
  search?: string;
  status?: SalesOrderStatus;
  customerId?: number;
  priceStatus?: SalesOrderPriceStatus;
}

export interface SalesOrderListResponse {
  data: SalesOrder[];
  pagination: {
    page: number;
    pageSize: number;
    total: number;
    totalPages: number;
  };
}

export interface CreateSalesOrderLineInput {
  productId: string;
  qty: number;
  /** Omit to auto-fill from the price resolver. */
  unitPrice?: number;
  /** Required when unitPrice deviates from the resolved price (manual entry). */
  overrideReason?: string;
  customerPartNumber?: string;
  discountPct?: number;
}

export interface CreateSalesOrderRequest {
  customerId: number;
  currency?: string;
  externalOrderNumber?: string;
  expectedShipDate?: string;
  deliveryDate?: string;
  lines: CreateSalesOrderLineInput[];
}

/** S2.3 modify flow — draft/confirmed only; later statuses are locked. */
export interface UpdateSalesOrderRequest {
  expectedShipDate?: string;
  deliveryDate?: string;
  currency?: string;
  /** Full replacement; replaced lines get NEW price snapshots. */
  lines?: CreateSalesOrderLineInput[];
}

export interface ReleasePriceHoldRequest {
  reason: string;
}

// ---------------------------------------------------------------------------
// Import (S2.4 intake drawer consumes these) — all-or-nothing + 訂單異常 queue
// ---------------------------------------------------------------------------

export type SalesOrderImportIssueCode =
  /** Part number matched neither internal SKU nor customer PN → queue. */
  | 'UNMATCHED_PART_NO'
  | 'MISSING_CUSTOMER'
  | 'MISSING_EXTERNAL_ORDER_NUMBER'
  | 'INVALID_QUANTITY'
  | 'INVALID_PRICE'
  | 'PRODUCT_NOT_READY'
  | 'PRODUCT_INACTIVE'
  /** Re-import hit an SO already past confirmed — update refused. */
  | 'SO_LOCKED';

export interface SalesOrderImportIssue {
  /** 1-based data row (header = row 1). */
  row: number;
  externalOrderNumber: string | null;
  code: SalesOrderImportIssueCode;
  /** Dev-facing detail; UI localizes from `code`. */
  message: string;
  partNumber?: string;
}

/** 訂單異常 queue entry — one per UNMATCHED_PART_NO row; S2.4 maps + reprocesses. */
export interface SalesOrderImportException {
  id: string;
  externalOrderNumber: string;
  customerId: number | null;
  /** The raw part number that matched nothing (never silently created). */
  partNumber: string;
  qty: number;
  unitPrice: number | null;
  row: number;
  status: 'open' | 'mapped' | 'dismissed';
  createdAt: string;
}

export interface ImportSalesOrdersRequest {
  /**
   * CSV: externalOrderNumber,customerId,partNumber,qty,unitPrice
   * [,expectedShipDate,deliveryDate] — header row skipped.
   */
  csv: string;
}

export interface ImportSalesOrdersResponse {
  /** Created + updated SOs (whole-SO granularity — all-or-nothing). */
  data: SalesOrder[];
  issues: SalesOrderImportIssue[];
  exceptions: SalesOrderImportException[];
  summary: {
    created: number;
    updated: number;
    failed: number;
  };
}
